/**
 * SPEC.md §7 — M6.2 (issue #29). URL hash sharing: `{ code, diagramTheme,
 * look, version: 1 }` deflate-compressed via `lz-string`'s
 * `compressToEncodedURIComponent`, carried as `#/<encoded>`.
 *
 * `diagramTheme`/`look` don't have real UI controls yet (M6.6/M6.7) — this
 * module's payload shape already includes them so those tickets extend
 * values, not the schema.
 */
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';

export interface ShareState {
  code: string;
  diagramTheme: string;
  look: string;
  version: 1;
}

/** SPEC.md §7's own "~8000 chars" ceiling, applied to the hash fragment (leading slash included). */
export const MAX_HASH_LENGTH = 8000;

function isShareState(value: unknown): value is ShareState {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.code === 'string' &&
    typeof v.diagramTheme === 'string' &&
    typeof v.look === 'string' &&
    v.version === 1
  );
}

/**
 * Returns the hash fragment (starting with `/`, everything after the URL's
 * `#`) for the given state, or `null` when the compressed result would
 * exceed `MAX_HASH_LENGTH` — the caller clears the hash entirely rather
 * than truncate a payload that wouldn't decode back correctly anyway.
 */
export function encodeShareState(state: ShareState): string | null {
  const fragment = `/${compressToEncodedURIComponent(JSON.stringify(state))}`;
  return fragment.length > MAX_HASH_LENGTH ? null : fragment;
}

/**
 * Returns the decoded state for a hash fragment (as `encodeShareState`
 * produces — `/<encoded>`), or `null` for anything that doesn't decode
 * cleanly: missing the leading slash, not valid lz-string output, not valid
 * JSON, the wrong shape, or an unrecognised `version`. A `null` here means
 * "fall through to the next load-precedence step," not an error — a stale,
 * foreign, or corrupted hash is an ordinary case, not a bug report.
 */
export function decodeShareState(hashFragment: string): ShareState | null {
  if (!hashFragment.startsWith('/')) return null;
  const encoded = hashFragment.slice(1);
  if (encoded.length === 0) return null;

  let decompressed: string | null;
  try {
    decompressed = decompressFromEncodedURIComponent(encoded);
  } catch {
    return null;
  }
  if (decompressed === null || decompressed === '') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(decompressed);
  } catch {
    return null;
  }

  return isShareState(parsed) ? parsed : null;
}
